import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { BuscaRemota, Entrada, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post, getTodos } from '../../nucleo/api';
import { dinheiro } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { invalidarEstoque, qtd, rotuloProduto } from './comum';
import type { Atributo, Catalogos, Variacao } from './tipos';

/** M9.1: atributos (cor, tamanho) e as variações dos produtos. */
export function Variacoes() {
  const { t } = useTranslation('estoque');
  const s = useSessao();
  const [aberto, setAberto] = useState<Variacao | 'nova' | null>(null);
  const [produtoFiltro, setProdutoFiltro] = useState<string | null>(null);

  const colunas: Coluna<Variacao>[] = [
    { chave: 'sku', rotulo: t('campos.sku'), mono: true, ordenavel: true, principal: true },
    {
      chave: 'produto_nome',
      rotulo: t('campos.produto'),
      render: (v) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate" title={v.produto_nome}>{v.produto_nome}</span>
          <span className="numero truncate text-xs text-tinta-3" title={v.produto_codigo}>{v.produto_codigo}</span>
        </span>
      ),
    },
    { chave: 'rotulo', rotulo: t('campos.combinacao'), render: (v) => v.rotulo },
    { chave: 'preco_efetivo_centavos', rotulo: t('campos.preco'), alinhar: 'direita', mono: true, render: (v) => dinheiro(v.preco_efetivo_centavos) },
    { chave: 'saldo', rotulo: t('campos.saldo'), alinhar: 'direita', mono: true, render: (v) => qtd(v.saldo) },
    { chave: 'ativo', rotulo: t('campos.situacao'), render: (v) => (v.ativo ? <Selo cor="verde">{t('status.ativo')}</Selo> : <Selo>{t('status.inativo')}</Selo>) },
  ];

  return (
    <div className="flex flex-col gap-5">
      {s.pode('estoque.criar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('nova')}>
            {t('acoes.novaVariacao')}
          </Botao>
        </AcaoPrimaria>
      )}
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Atributos />
        </div>
        <Unidades />
      </div>
      <div className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-tinta-1">{t('variacao.titulo')}</h2>
        <div className="max-w-md">
          <BuscaRemota
            id="filtro-produto-variacao"
            def={{ caminho: '/estoque/produtos', rotulo: rotuloProduto }}
            valor={produtoFiltro}
            aoMudar={(v) => setProdutoFiltro(v)}
          />
        </div>
        <Tabela<Variacao>
          tela="estoque.variacoes"
          caminho="/estoque/variacoes"
          params={{ produto_id: produtoFiltro || undefined }}
          filtros={{ produto_id: produtoFiltro ?? '' }}
          aoAplicarFiltros={(f) => setProdutoFiltro(String(f.produto_id) || null)}
          colunas={colunas}
          aoClicar={(v) => setAberto(v)}
          vazio={<Vazio texto={t('vazio.variacoes')} />}
        />
      </div>
      <DrawerVariacao aberto={aberto} aoFechar={() => setAberto(null)} />
    </div>
  );
}

/** Unidades de medida do núcleo, só leitura: o produto escolhe uma delas. */
function Unidades() {
  const { t } = useTranslation('estoque');
  const q = useQuery({ queryKey: ['estoque', 'catalogos'], queryFn: () => get<Catalogos>('/nucleo/catalogos'), staleTime: 300_000 });
  return (
    <Cartao titulo={t('unidade.titulo')}>
      <p className="mb-3 text-sm text-tinta-3">{t('unidade.texto')}</p>
      <Estado consulta={q} vazio={(d) => d.unidades.length === 0}>
        {(d) => (
          <ul className="divide-y divide-borda text-sm">
            {d.unidades.map((u) => (
              <li key={u.codigo} className="flex items-center justify-between gap-3 py-1.5">
                <span>
                  <span className="numero font-medium">{u.codigo}</span>
                  <span className="text-tinta-3">{` ${u.nome}`}</span>
                </span>
                <span className="text-xs text-tinta-3">{u.tipo}</span>
              </li>
            ))}
          </ul>
        )}
      </Estado>
    </Cartao>
  );
}

function Atributos() {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [nome, setNome] = useState('');
  const [valores, setValores] = useState('');
  const [novoValor, setNovoValor] = useState<Record<string, string>>({});
  const [apagar, setApagar] = useState<Atributo | null>(null);
  const [apagarValor, setApagarValor] = useState<{ atributo: string; id: string; valor: string } | null>(null);
  const q = useQuery({ queryKey: ['/estoque/atributos'], queryFn: () => getTodos<Atributo>('/estoque/atributos') });
  const recarregar = () => invalidarEstoque(qc);
  const erro = (e: unknown) => toast.erro(mensagemDeErro(e));

  const criar = useMutation({
    mutationFn: () =>
      post('/estoque/atributos', {
        nome,
        valores: valores
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setNome('');
      setValores('');
      return recarregar();
    },
    onError: erro,
  });
  const addValor = useMutation({
    mutationFn: (v: { atributo: string; valor: string }) => post(`/estoque/atributos/${v.atributo}/valores`, { valor: v.valor }),
    onSuccess: (_r, v) => {
      setNovoValor((x) => ({ ...x, [v.atributo]: '' }));
      return recarregar();
    },
    onError: erro,
  });
  const rmValor = useMutation({
    mutationFn: (v: { atributo: string; id: string }) => del(`/estoque/atributos/${v.atributo}/valores/${v.id}`),
    onSuccess: () => {
      setApagarValor(null);
      return recarregar();
    },
    onError: (e) => {
      setApagarValor(null);
      erro(e);
    },
  });
  const rmAtributo = useMutation({
    mutationFn: (id: string) => del(`/estoque/atributos/${id}`),
    onSuccess: () => {
      setApagar(null);
      return recarregar();
    },
    onError: (e) => {
      setApagar(null);
      erro(e);
    },
  });

  return (
    <Cartao titulo={t('atributo.titulo')}>
      {s.pode('estoque.criar') && (
        <form
          className="mb-4 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (nome.trim()) criar.mutate();
          }}
        >
          <Entrada aria-label={t('campos.nomeAtributo')} placeholder={t('atributo.novoNome')} value={nome} onChange={(e) => setNome(e.target.value)} className="min-w-0 flex-1 basis-40" maxLength={60} />
          <Entrada aria-label={t('campos.valores')} placeholder={t('atributo.valoresIniciais')} value={valores} onChange={(e) => setValores(e.target.value)} className="min-w-0 flex-1 basis-56" />
          <Botao type="submit" variante="secundario" icone={<Plus className="size-4" />} carregando={criar.isPending}>
            {t('acoes.adicionar')}
          </Botao>
        </form>
      )}
      <Estado consulta={q} vazio={(d) => d.itens.length === 0}>
        {(d) => (
          <ul className="divide-y divide-borda">
            {d.itens.map((a) => (
              <li key={a.id} className="flex flex-col gap-2 py-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 font-medium wrap-anywhere">{a.nome}</span>
                  {s.pode('estoque.excluir') && (
                    <button type="button" aria-label={tn('comum.excluir')} onClick={() => setApagar(a)} className="rounded p-1 text-tinta-3 hover:text-perigo">
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {a.valores.map((v) => (
                    <span key={v.id} className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-borda px-2.5 py-0.5 text-xs text-tinta-2">
                      <span className="min-w-0 wrap-anywhere">{v.valor}</span>
                      {s.pode('estoque.excluir') && (
                        // r8 visual B6: alvo de 24px (era o X de 12px) e exclusão com confirmação.
                        <button type="button" aria-label={`${tn('comum.excluir')} ${v.valor}`} onClick={() => setApagarValor({ atributo: a.id, id: v.id, valor: v.valor })} className="-my-1 -mr-1.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full text-tinta-3 hover:bg-sup-3 hover:text-perigo">
                          <X className="size-3.5" aria-hidden />
                        </button>
                      )}
                    </span>
                  ))}
                  {s.pode('estoque.editar') && (
                    <form
                      className="flex items-center gap-1"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const valor = (novoValor[a.id] ?? '').trim();
                        if (valor) addValor.mutate({ atributo: a.id, valor });
                      }}
                    >
                      <Entrada aria-label={t('atributo.novoValor')} placeholder={t('atributo.novoValor')} value={novoValor[a.id] ?? ''} onChange={(e) => setNovoValor((x) => ({ ...x, [a.id]: e.target.value }))} className="h-8 w-32" maxLength={60} />
                      <Botao type="submit" tamanho="sm" aria-label={t('acoes.adicionar')} icone={<Plus className="size-4" />} />
                    </form>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Estado>
      <Confirmar aberto={!!apagarValor} aoFechar={() => setApagarValor(null)} aoConfirmar={() => apagarValor && rmValor.mutate(apagarValor)} carregando={rmValor.isPending} perigoso titulo={t('confirmar.excluirValor', { valor: apagarValor?.valor ?? '' })} texto={t('confirmar.excluirValorTexto')} />
      <Confirmar aberto={!!apagar} aoFechar={() => setApagar(null)} aoConfirmar={() => apagar && rmAtributo.mutate(apagar.id)} carregando={rmAtributo.isPending} perigoso titulo={t('confirmar.excluirAtributo')} texto={t('confirmar.excluirAtributoTexto')} />
    </Cartao>
  );
}

function DrawerVariacao({ aberto, aoFechar }: { aberto: Variacao | 'nova' | null; aoFechar: () => void }) {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const atual = aberto && aberto !== 'nova' ? aberto : null;
  const [apagar, setApagar] = useState(false);
  const [attrs, setAttrs] = useState<Record<string, string>>({});
  useEffect(() => {
    setAttrs(aberto && aberto !== 'nova' ? { ...aberto.valores } : {});
  }, [aberto]);
  const atributos = useQuery({ queryKey: ['/estoque/atributos'], queryFn: () => getTodos<Atributo>('/estoque/atributos'), enabled: !!aberto });

  const campos: DefCampo[] = [
    ...(atual
      ? []
      : [
          {
            nome: 'produto_id',
            rotulo: t('campos.produto'),
            tipo: 'busca' as const,
            obrigatorio: true,
            busca: { caminho: '/estoque/produtos', rotulo: rotuloProduto },
          },
        ]),
    { nome: 'sku', rotulo: t('campos.sku'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano', largura: 'meia' },
    { nome: 'preco_centavos', rotulo: t('campos.precoProprio'), tipo: 'dinheiro', largura: 'meia', ajuda: t('ajuda.precoVariacao') },
    { nome: 'custo_centavos', rotulo: t('campos.custoProprio'), tipo: 'dinheiro', largura: 'meia' },
  ];

  const remover = useMutation({
    mutationFn: () => del(`/estoque/variacoes/${atual!.id}`),
    onSuccess: async () => {
      await invalidarEstoque(qc);
      setApagar(false);
      aoFechar();
    },
    onError: (e) => {
      setApagar(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const inicial: Record<string, unknown> = atual ? { ...atual } : { ativo: true };

  return (
    <>
      <Drawer
        aberto={!!aberto && !!atributos.data}
        aoFechar={aoFechar}
        titulo={atual ? atual.sku : t('acoes.novaVariacao')}
        rodape={
          <>
            {atual && s.pode('estoque.excluir') && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setApagar(true)} className="mr-auto">
                {tn('comum.excluir')}
              </Botao>
            )}
            {(atual ? s.pode('estoque.editar') : s.pode('estoque.criar')) && (
              <>
                <Botao variante="fantasma" onClick={aoFechar}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-variacao">
                  {tn('comum.salvar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        {atual && (
          <p className="mb-4 text-sm text-tinta-3">
            {atual.produto_codigo} {atual.produto_nome}
          </p>
        )}
        {atributos.data && atributos.data.itens.length === 0 && <p className="mb-4 text-sm text-laranja">{t('variacao.semAtributos')}</p>}
        <Formulario
          key={atual?.id ?? 'nova'}
          id="form-variacao"
          campos={campos}
          inicial={inicial}
          aoEnviar={async (v) => {
            const combinacao: Record<string, string> = {};
            for (const a of atributos.data?.itens ?? []) {
              const escolhido = attrs[a.id];
              if (escolhido) combinacao[a.id] = escolhido;
            }
            const corpo = { sku: v.sku, valores: combinacao, preco_centavos: v.preco_centavos ?? null, custo_centavos: v.custo_centavos ?? null, ativo: v.ativo !== false };
            if (atual) await patch(`/estoque/variacoes/${atual.id}`, corpo);
            else await post('/estoque/variacoes', { ...corpo, produto_id: v.produto_id });
            await invalidarEstoque(qc);
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        >
          {(atributos.data?.itens ?? []).map((a) => (
            <div key={a.id} className="sm:col-span-3">
              <Rotulo id={`attr-${a.id}`} rotulo={a.nome}>
                <Selecao
                  id={`attr-${a.id}`}
                  vazio={t('variacao.selecione')}
                  opcoes={a.valores.map((v) => ({ valor: v.id, rotulo: v.valor }))}
                  value={attrs[a.id] ?? ''}
                  onChange={(e) => setAttrs((x) => ({ ...x, [a.id]: e.target.value }))}
                />
              </Rotulo>
            </div>
          ))}
        </Formulario>
      </Drawer>
      <Confirmar aberto={apagar} aoFechar={() => setApagar(false)} aoConfirmar={() => remover.mutate()} carregando={remover.isPending} perigoso titulo={t('confirmar.excluirVariacao')} texto={t('confirmar.excluirVariacaoTexto')} />
    </>
  );
}
